import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '../components/ui';
import PublicNav from '../components/layout/PublicNav';
import NewsletterSignup from '../components/NewsletterSignup';

const landingLinks = [
  { label: 'Features', to: '#features' },
  { label: 'How It Works', to: '#how-it-works' },
  { label: 'Pricing', to: '#pricing' },
  { label: 'Blog', to: '/blog' },
  { label: 'Support', to: '/support' },
];

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[var(--theme-bg-primary)] text-text-primary flex flex-col">
      <PublicNav links={landingLinks} />

      {/* Hero Section */}
      <section className="md:pt-28 md:pb-16 text-center bg-[var(--theme-bg-secondary)] pt-24 sm:pt-40 pb-12 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-[700px] mx-auto">
          <h1 className="md:text-[32px] font-bold leading-tight mb-6 text-text-primary text-[28px] sm:text-5xl">Run your dynamic.</h1>
          <p className="text-base md:text-xl text-text-secondary leading-relaxed mb-10">
            Contracts, protocols, check-ins, drop detection, scene planning, aftercare
            -- in one private space. Built by someone who got tired of running a 24/7
            in a Notes app.
          </p>
          <div className="flex flex-col gap-4 justify-center mb-6 md:flex-row">
            <Link to="/register">
              <Button variant="primary" size="lg" className="bg-gradient-to-r from-crimson to-bronze hover:from-crimson/90 hover:to-bronze/90">Try Helm free</Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary" size="lg">Sign In</Button>
            </Link>
          </div>
          <p className="text-sm text-text-tertiary">No credit card required. Free plan available.</p>
        </div>
      </section>

      {/* Features Section - Four Pillars */}
      <section id="features" className="py-16 md:py-24 px-6 bg-[var(--theme-bg-primary)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-[28px] md:text-4xl font-bold text-center mb-16 text-text-primary">Your dynamic deserves better than a spreadsheet.</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card tone="secondary" radius="lg" padding="none" className="p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg border-t-[3px] border-t-olive">
              <div className="w-12 h-12 bg-olive rounded-lg flex items-center justify-center mb-5 text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Cultivate</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">Mutual-reveal negotiation. Both fill in limits independently. No anchoring bias. Verified profiles, privacy controls, and incognito mode -- you decide who sees you.</p>
            </Card>

            <Card tone="secondary" radius="lg" padding="none" className="p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg border-t-[3px] border-t-aegean">
              <div className="w-12 h-12 bg-aegean rounded-lg flex items-center justify-center mb-5 text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Connect</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">Channels with real moderation. Content warnings, not censorship. Mentorship, private DMs, and threaded discussions -- people beyond just your dynamic.</p>
            </Card>

            <Card tone="secondary" radius="lg" padding="none" className="p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg border-t-[3px] border-t-crimson">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-5 text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Command</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">Contracts that live where your relationship does. Protocol compliance you can actually track. Tasks, training, rituals, rewards, and consequences -- structured, not scattered.</p>
            </Card>

            <Card tone="secondary" radius="lg" padding="none" className="p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg border-t-[3px] border-t-gold">
              <div className="w-12 h-12 bg-gold rounded-lg flex items-center justify-center mb-5 text-ink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Chronicle</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">8-signal drop detection. Rolling window. Catches it before it spirals. Private journals, mood tracking, scene logs -- your dynamic, documented.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 px-6 bg-[var(--theme-bg-secondary)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-[28px] md:text-4xl font-bold text-center mb-4 text-text-primary">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-aegean text-white rounded-full flex items-center justify-center text-[28px] font-bold mx-auto mb-6">1</div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Verify once</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">Quick biometric verification confirms you are real and 18+. It takes seconds and is never stored.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-aegean text-white rounded-full flex items-center justify-center text-[28px] font-bold mx-auto mb-6">2</div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Discover</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">Browse profiles, match with compatible partners, or join channels that share your interests.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-aegean text-white rounded-full flex items-center justify-center text-[28px] font-bold mx-auto mb-6">3</div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Structure your dynamic</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed">Create your hierarchy, set protocols, track compliance, and run check-ins -- everything connected.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Privacy Section */}
      <section className="py-16 md:py-24 px-6 bg-[var(--theme-bg-primary)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-[28px] md:text-4xl font-bold text-center mb-4 text-text-primary">Privacy is infrastructure, not a feature.</h2>
          <p className="text-lg text-text-secondary text-center max-w-[600px] mx-auto mb-16">
            Verification protects everyone -- whether you are matching with someone new or building a dynamic with your partner.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="w-14 h-14 bg-aegean rounded-full flex items-center justify-center mx-auto mb-5 text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <path d="M20 8v6" />
                  <path d="M23 11h-6" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Identity Verification</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed max-w-[280px] mx-auto">Every member is verified as a real person, 18+. No bots, no catfishing, no guesswork.</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-aegean rounded-full flex items-center justify-center mx-auto mb-5 text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">Your Data Stays Yours</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed max-w-[280px] mx-auto">Biometric scans are processed by our verification partner and never stored on our servers. Your content is encrypted and private.</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-aegean rounded-full flex items-center justify-center mx-auto mb-5 text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-primary">You Control Visibility</h3>
              <p className="text-[15px] text-text-secondary leading-relaxed max-w-[280px] mx-auto">Incognito mode, private albums, and granular privacy settings. You decide who sees what, always.</p>
            </div>
          </div>
        </div>
      </section>

      {/* No Ads Promise */}
      <section className="py-16 md:py-20 px-6 bg-[var(--theme-bg-elevated)] text-center border-y border-border-light">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-[28px] md:text-[32px] font-bold mb-5 text-text-primary">No ads. No data selling. Ever.</h2>
          <p className="text-lg text-text-secondary text-center max-w-[700px] mx-auto">
            Your data stays on our servers and is never shared with third parties. Helm is funded entirely by subscriptions because your privacy is not negotiable. When you subscribe, you are keeping Helm independent and ad-free.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 px-6 bg-[var(--theme-bg-primary)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-[28px] md:text-4xl font-bold text-center mb-4 text-text-primary">Simple, transparent pricing</h2>
          <p className="text-lg text-text-secondary text-center max-w-[600px] mx-auto mb-16">
            Start free. Subscribe to unlock more and help keep Helm ad-free.
          </p>

          <div className="grid grid-cols-1 max-w-[400px] lg:grid-cols-4 lg:max-w-none gap-6 md:max-w-[1200px] mx-auto">
            <Card tone="secondary" padding="none" className="p-8 border-2 border-transparent hover:border-border-default transition-all">
              <h3 className="text-2xl font-semibold mb-4 text-text-primary">Free</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-text-primary">$0</span>
                <span className="text-base text-text-secondary">/forever</span>
              </div>
              <p className="text-sm text-text-secondary mb-6 min-h-[42px]">Try the core loop.</p>
              <ul className="list-none p-0 mb-6">
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">2 hierarchy members</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">5 active tasks</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">1 contract</li>
                <li className="py-2.5 text-sm text-text-secondary flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">100 MB storage</li>
              </ul>
              <Link to="/register">
                <Button variant="secondary" fullWidth>Start free</Button>
              </Link>
            </Card>

            <Card tone="elevated" padding="none" className="p-8 border-2 border-accent shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-aegean text-white px-4 py-1.5 rounded-xl text-[13px] font-semibold">Popular</div>
              <h3 className="text-2xl font-semibold mb-4 text-text-primary">Core</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-text-primary">$50</span>
                <span className="text-base text-text-secondary">/year</span>
              </div>
              <p className="text-sm text-text-secondary mb-6 min-h-[42px]">Run your dynamic.</p>
              <ul className="list-none p-0 mb-6">
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">5 hierarchy members</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">25 active tasks</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">1 GB storage</li>
                <li className="py-2.5 text-sm text-text-secondary flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">Scene logs and journaling</li>
              </ul>
              <Link to="/register">
                <Button variant="primary" fullWidth className="bg-gradient-to-r from-crimson to-bronze hover:from-crimson/90 hover:to-bronze/90">Try Helm free</Button>
              </Link>
            </Card>

            <Card tone="secondary" padding="none" className="p-8 border-2 border-transparent hover:border-border-default transition-all">
              <h3 className="text-2xl font-semibold mb-4 text-text-primary">Pro</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-text-primary">$90</span>
                <span className="text-base text-text-secondary">/year</span>
              </div>
              <p className="text-sm text-text-secondary mb-6 min-h-[42px]">Full platform access.</p>
              <ul className="list-none p-0 mb-6">
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">Unlimited hierarchy members</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">Unlimited tasks and contracts</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">10 GB storage</li>
                <li className="py-2.5 text-sm text-text-secondary flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">Training programs and mentorship</li>
              </ul>
              <Link to="/register">
                <Button variant="secondary" fullWidth>Start your dynamic</Button>
              </Link>
            </Card>

            <Card tone="secondary" padding="none" className="p-8 border-2 border-transparent hover:border-border-default transition-all">
              <h3 className="text-2xl font-semibold mb-4 text-text-primary">Leader</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-text-primary">$130</span>
                <span className="text-base text-text-secondary">/year</span>
              </div>
              <p className="text-sm text-text-secondary mb-6 min-h-[42px]">Full platform, unlimited scale.</p>
              <ul className="list-none p-0 mb-6">
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">Unlimited everything</li>
                <li className="py-2.5 text-sm text-text-secondary border-b border-border-light flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">25 GB storage</li>
                <li className="py-2.5 text-sm text-text-secondary flex items-center gap-2 before:content-['✓'] before:text-success before:font-semibold">Full API access</li>
              </ul>
              <Link to="/register">
                <Button variant="secondary" fullWidth>Start your dynamic</Button>
              </Link>
            </Card>
          </div>

          <p className="text-sm text-text-secondary text-center mt-8">
            <Link to="/pricing" className="text-accent hover:underline">See the full plan comparison</Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--theme-bg-inverse)] text-[var(--theme-text-inverse)] py-16 px-6 pb-[calc(24px+env(safe-area-inset-bottom,0px))]">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-10 md:gap-16">
          <div>
            <span className="block text-2xl font-bold text-[var(--theme-text-inverse)] mb-4">Helm</span>
            <p className="text-[var(--theme-text-inverse-secondary)] text-sm">Structure for what matters.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <h4 className="text-sm font-semibold mb-4 text-[var(--theme-text-inverse)]">Product</h4>
              <a href="#features" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Features</a>
              <Link to="/blog" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Blog</Link>
              <a href="#pricing" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Pricing</a>
              <Link to="/register" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Sign Up</Link>
              <Link to="/login" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Log In</Link>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-[var(--theme-text-inverse)]">Support</h4>
              <Link to="/support" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Help Center</Link>
              <a href="mailto:support@submitlist.space" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Contact Us</a>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-[var(--theme-text-inverse)]">Legal</h4>
              <Link to="/privacy-policy" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Terms of Service</Link>
              <Link to="/data-deletion" className="block text-[var(--theme-text-inverse-secondary)] no-underline text-sm py-1.5 hover:text-[var(--theme-text-inverse)] transition-colors">Data Deletion</Link>
            </div>
          </div>

          <div className="col-span-full mt-6 pt-8 border-t border-[var(--theme-border-inverse)]">
            <NewsletterSignup source="landing-footer" />
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto mt-12 pt-6 border-t border-[var(--theme-border-inverse)] text-center">
          <p className="text-[var(--theme-text-inverse-tertiary)] text-sm">&copy; {new Date().getFullYear()} Helm. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
